// Real Request Access form. Wires to POST /api/public/signup-requests.
// Cloudflare Turnstile + honeypot. Inline validation. Coral submit.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import Seo from '../components/Seo';
import { API_BASE_URL as API_BASE } from '../lib/api-url';

const TURNSTILE_SITE_KEY = '0x4AAAAAAC1ls6HHUsVkYqNC';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France',
  'Netherlands', 'Spain', 'Italy', 'Sweden', 'Norway', 'Denmark', 'Ireland',
  'New Zealand', 'Singapore', 'Japan', 'South Korea', 'Mexico', 'Brazil', 'Other',
];

const INDUSTRIES = [
  'Apparel & Fashion', 'Beauty & Cosmetics', 'Home & Garden', 'Food & Beverage',
  'Health & Wellness', 'Electronics', 'Jewelry & Accessories', 'Sports & Outdoors',
  'Art & Crafts', 'Pet Supplies', 'Books & Media', 'Toys & Games',
  'Professional Services', 'Other',
];

const SIZES = [
  { v: '1-5', label: '1 to 5' },
  { v: '6-20', label: '6 to 20' },
  { v: '21-50', label: '21 to 50' },
  { v: '51-200', label: '51 to 200' },
  { v: '200+', label: '200+' },
];

const VOLUMES = [
  { v: '<500', label: 'Under 500' },
  { v: '500-2k', label: '500 to 2,000' },
  { v: '2k-10k', label: '2,000 to 10,000' },
  { v: '10k-50k', label: '10,000 to 50,000' },
  { v: '50k+', label: '50,000+' },
];

const CHANNELS = [
  { v: 'instagram', label: 'Instagram' },
  { v: 'facebook', label: 'Facebook' },
  { v: 'whatsapp', label: 'WhatsApp' },
  { v: 'tiktok', label: 'TikTok' },
  { v: 'email', label: 'Email' },
  { v: 'sms', label: 'SMS' },
  { v: 'web', label: 'Web widget' },
  { v: 'shopify', label: 'Shopify' },
  { v: 'woocommerce', label: 'WooCommerce' },
  { v: 'wordpress', label: 'WordPress' },
];

const PLATFORMS = [
  { v: 'shopify', label: 'Shopify' },
  { v: 'woocommerce', label: 'WooCommerce' },
  { v: 'wordpress', label: 'WordPress' },
  { v: 'custom', label: 'Custom' },
  { v: 'none', label: 'None yet' },
];

const HOW_HEARD = [
  'Google search',
  'Resin Rock customer',
  'Referral',
  'Social media',
  'Industry event',
  'Other',
];

type FormState = {
  full_name: string;
  email: string;
  business_name: string;
  website_url: string;
  country: string;
  industry: string;
  business_size: string;
  expected_monthly_volume: string;
  channels_interest: string[];
  platform_type: string;
  how_heard: string;
  message: string;
  website_field: string; // honeypot
};

const initialState: FormState = {
  full_name: '',
  email: '',
  business_name: '',
  website_url: '',
  country: '',
  industry: '',
  business_size: '',
  expected_monthly_volume: '',
  channels_interest: [],
  platform_type: '',
  how_heard: '',
  message: '',
  website_field: '',
};

export default function RequestAccess() {
  const [, setLocation] = useLocation();
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Load Turnstile script + render widget once
  useEffect(() => {
    const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'dark',
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
      if (!existing) {
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.onload = renderWidget;
        document.head.appendChild(s);
      } else {
        existing.addEventListener('load', renderWidget);
      }
    }

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function toggleChannel(v: string) {
    setForm((f) => {
      const has = f.channels_interest.includes(v);
      return {
        ...f,
        channels_interest: has
          ? f.channels_interest.filter((c) => c !== v)
          : [...f.channels_interest, v],
      };
    });
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.full_name.trim()) next.full_name = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Enter a valid email';
    if (!form.business_name.trim()) next.business_name = 'Required';
    if (form.website_url.trim() && !/^https?:\/\/.+/i.test(form.website_url.trim())) {
      next.website_url = 'Must start with http:// or https://';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (!validate()) return;
    if (!turnstileToken) {
      setServerError('Please complete the captcha below.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/signup-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          business_name: form.business_name.trim(),
          website_url: form.website_url.trim() || undefined,
          country: form.country || undefined,
          industry: form.industry || undefined,
          business_size: form.business_size || undefined,
          expected_monthly_volume: form.expected_monthly_volume || undefined,
          channels_interest: form.channels_interest.length ? form.channels_interest : undefined,
          platform_type: form.platform_type || undefined,
          how_heard: form.how_heard || undefined,
          message: form.message.trim() || undefined,
          website_field: form.website_field,
          turnstile_token: turnstileToken,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; id?: string; error?: string };
      if (!res.ok || !data.ok) {
        setServerError(data.error || 'Submission failed. Please try again.');
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
          setTurnstileToken('');
        }
        return;
      }
      setLocation(`/request-submitted?id=${encodeURIComponent(data.id || '')}`);
    } catch {
      setServerError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputBase =
    'w-full rounded-input border border-ink-3 bg-ink-2 px-4 py-3 text-sm text-white placeholder:text-graphite focus:border-signal focus:outline-none focus:ring-1 focus:ring-signal/40 transition';
  const selectBase =
    inputBase +
    ' appearance-none bg-no-repeat pr-11 cursor-pointer';
  const selectStyle = {
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%237CFFB8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
    backgroundPosition: 'right 1rem center',
    backgroundSize: '14px 14px',
    colorScheme: 'dark' as const,
  };
  const labelBase = 'block font-mono text-mono-xs uppercase tracking-mono text-ash mb-2';
  const errorText = 'mt-1.5 text-xs text-[#FF8A8A]';

  const pillBase = 'rounded-pill border-2 px-4 py-2 text-xs font-semibold transition select-none';
  const pillActive = 'border-signal text-ink shadow-[0_0_24px_rgba(124,255,184,0.35)]';
  const pillInactive = 'border-ink-3 bg-ink-2 text-ash hover:border-signal/50 hover:text-white';
  const pillActiveStyle = { background: '#7CFFB8' };

  return (
    <section className="relative isolate overflow-hidden bg-ink text-white">
      <Seo
        title="Request access · WFS Chatbot"
        description="Tell us about your business. We review every request manually. Most decisions land within two business days. No credit card to apply."
        path="/request-access"
      />
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div
        className="absolute -top-32 right-0 -z-10 h-[480px] w-[480px] rounded-full opacity-[0.06] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />
      <div className="mx-auto max-w-form px-6 py-20 md:py-28">
        <span className="rr-tag">// request access</span>
        <h1 className="mt-4 font-bold tracking-headline text-display-lg text-white">
          Request access.
        </h1>
        <p className="mt-4 text-base text-ash md:text-lg">
          Tell us about your business. We review every request manually and most decisions land within two business days.
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-12 space-y-6">
          {/* Honeypot — hidden from users */}
          <div aria-hidden className="absolute left-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
            <label htmlFor="website_field">Leave this blank</label>
            <input
              id="website_field"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website_field}
              onChange={(e) => update('website_field', e.target.value)}
            />
          </div>

          <div>
            <label className={labelBase} htmlFor="full_name">Full name *</label>
            <input
              id="full_name"
              type="text"
              autoComplete="name"
              className={inputBase}
              value={form.full_name}
              onChange={(e) => update('full_name', e.target.value)}
            />
            {errors.full_name && <p className={errorText}>{errors.full_name}</p>}
          </div>

          <div>
            <label className={labelBase} htmlFor="email">Work email *</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={inputBase}
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
            />
            {errors.email && <p className={errorText}>{errors.email}</p>}
          </div>

          <div>
            <label className={labelBase} htmlFor="business_name">Business name *</label>
            <input
              id="business_name"
              type="text"
              autoComplete="organization"
              className={inputBase}
              value={form.business_name}
              onChange={(e) => update('business_name', e.target.value)}
            />
            {errors.business_name && <p className={errorText}>{errors.business_name}</p>}
          </div>

          <div>
            <label className={labelBase} htmlFor="website_url">Website URL</label>
            <input
              id="website_url"
              type="url"
              placeholder="https://example.com"
              autoComplete="url"
              className={inputBase}
              value={form.website_url}
              onChange={(e) => update('website_url', e.target.value)}
            />
            {errors.website_url && <p className={errorText}>{errors.website_url}</p>}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <label className={labelBase} htmlFor="country">Country</label>
              <select
                id="country"
                className={selectBase}
                style={selectStyle}
                value={form.country}
                onChange={(e) => update('country', e.target.value)}
              >
                <option value="">Select...</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelBase} htmlFor="industry">Industry</label>
              <select
                id="industry"
                className={selectBase}
                style={selectStyle}
                value={form.industry}
                onChange={(e) => update('industry', e.target.value)}
              >
                <option value="">Select...</option>
                {INDUSTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <span className={labelBase}>Business size</span>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => {
                const active = form.business_size === s.v;
                return (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => update('business_size', s.v)}
                    className={`${pillBase} ${active ? pillActive : pillInactive}`}
                    style={active ? pillActiveStyle : undefined}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className={labelBase}>Expected monthly conversations</span>
            <div className="flex flex-wrap gap-2">
              {VOLUMES.map((v) => {
                const active = form.expected_monthly_volume === v.v;
                return (
                  <button
                    key={v.v}
                    type="button"
                    onClick={() => update('expected_monthly_volume', v.v)}
                    className={`${pillBase} ${active ? pillActive : pillInactive}`}
                    style={active ? pillActiveStyle : undefined}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className={labelBase}>Channels of interest</span>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((ch) => {
                const active = form.channels_interest.includes(ch.v);
                return (
                  <button
                    key={ch.v}
                    type="button"
                    onClick={() => toggleChannel(ch.v)}
                    aria-pressed={active}
                    className={`${pillBase} ${active ? pillActive : pillInactive}`}
                    style={active ? pillActiveStyle : undefined}
                  >
                    {ch.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className={labelBase}>Platform</span>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS.map((p) => {
                const active = form.platform_type === p.v;
                return (
                  <button
                    key={p.v}
                    type="button"
                    onClick={() => update('platform_type', p.v)}
                    className={`${pillBase} ${active ? pillActive : pillInactive}`}
                    style={active ? pillActiveStyle : undefined}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={labelBase} htmlFor="how_heard">How did you hear about us?</label>
            <select
              id="how_heard"
              className={selectBase}
              style={selectStyle}
              value={form.how_heard}
              onChange={(e) => update('how_heard', e.target.value)}
            >
              <option value="">Select...</option>
              {HOW_HEARD.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelBase} htmlFor="message">Anything else?</label>
            <textarea
              id="message"
              rows={4}
              className={inputBase}
              placeholder="Tell us anything that helps us review your request faster."
              value={form.message}
              onChange={(e) => update('message', e.target.value)}
            />
          </div>

          {/* Cloudflare Turnstile */}
          <div>
            <span className={labelBase}>Verify you&apos;re human</span>
            <div ref={turnstileRef} />
          </div>

          {serverError && (
            <div className="rounded-input border border-[#FF8A8A]/40 bg-[#FF8A8A]/5 px-4 py-3 text-sm text-[#FFB3B3]">
              {serverError}
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-pill px-8 py-4 text-sm font-bold transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ background: '#FF8A6B', color: '#0E1316' }}
            >
              {submitting ? 'Submitting...' : 'Submit request'}
            </button>
            <p className="mt-3 font-mono text-mono-xs uppercase tracking-mono text-graphite">
              manually reviewed · 2 business days · no credit card
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
